import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  FaPlus, FaArrowLeft, FaPaperPlane, FaCircle, FaCheckCircle,
  FaLifeRing, FaTimes, FaClock,
} from 'react-icons/fa';
import { Toaster } from 'react-hot-toast';
import { useProContext } from '../context/ProContext';
import ProLayout from '../layout/ProLayout';
import { showToast } from '../utils/toast';
import api from '../../../services/api';

const SupportPage = () => {
  const { currentUser, brand, brandName } = useProContext();

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [thread, setThread] = useState(null);
  const [threadLoading, setThreadLoading] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);

  // New ticket form
  const [showNewForm, setShowNewForm] = useState(false);
  const [newSubject, setNewSubject] = useState('');
  const [newBody, setNewBody] = useState('');
  const [creating, setCreating] = useState(false);

  const messagesEndRef = useRef(null);

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  });

  const fetchTickets = useCallback(async () => {
    try {
      const { data } = await api.get('/support/tickets', { headers: authHeaders() });
      setTickets(Array.isArray(data) ? data : []);
    } catch { setTickets([]); }
    setLoading(false);
  }, []);

  const fetchThread = useCallback(async (id) => {
    setThreadLoading(true);
    try {
      const { data } = await api.get(`/support/tickets/${id}`, { headers: authHeaders() });
      setThread(data);
      // Refresh tickets list to update unread status
      fetchTickets();
    } catch { setThread(null); }
    setThreadLoading(false);
  }, [fetchTickets]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);
  useEffect(() => {
    if (selectedId) fetchThread(selectedId);
    else setThread(null);
  }, [selectedId, fetchThread]);

  useEffect(() => {
    if (thread && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [thread]);

  const createTicket = async () => {
    if (!newSubject.trim() || !newBody.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post('/support/tickets',
        { subject: newSubject, message: newBody },
        { headers: authHeaders() }
      );
      showToast.success('Ticket créé. Nous allons vous répondre rapidement.');
      setNewSubject('');
      setNewBody('');
      setShowNewForm(false);
      await fetchTickets();
      setSelectedId(data.id);
    } catch (e) {
      showToast.error('Erreur lors de la création');
    }
    setCreating(false);
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedId) return;
    setSending(true);
    try {
      await api.post(`/support/tickets/${selectedId}/messages`,
        { content: newMessage },
        { headers: authHeaders() }
      );
      setNewMessage('');
      await fetchThread(selectedId);
    } catch (e) {
      showToast.error('Erreur lors de l\'envoi');
    }
    setSending(false);
  };

  const closeTicket = async () => {
    if (!selectedId) return;
    if (!window.confirm('Êtes-vous sûr de vouloir clore ce ticket ?')) return;
    try {
      await api.put(`/support/tickets/${selectedId}/close`, {}, { headers: authHeaders() });
      showToast.success('Ticket fermé');
      await fetchThread(selectedId);
      await fetchTickets();
    } catch {
      showToast.error('Erreur');
    }
  };

  const formatDate = (d) => {
    const date = new Date(d);
    const now = new Date();
    const diffH = Math.floor((now - date) / 3600000);
    if (diffH < 1) return "À l'instant";
    if (diffH < 24) return `Il y a ${diffH}h`;
    if (diffH < 48) return 'Hier';
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  const formatMessageDate = (d) => {
    const date = new Date(d);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <ProLayout title="Support">
      <Toaster />
      <div className="h-full flex overflow-hidden">

        {/* ═══ LEFT: Tickets list ═══ */}
        <div className="w-80 flex-shrink-0 border-r border-gray-200 bg-white flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <h3 className="text-sm font-bold text-gray-900">Mes tickets</h3>
            <button
              onClick={() => { setShowNewForm(true); setSelectedId(null); }}
              className="flex items-center gap-1.5 px-3 py-1.5 text-[0.6875rem] font-semibold text-white rounded-lg hover:opacity-90"
              style={{ backgroundColor: brand }}
            >
              <FaPlus className="w-2.5 h-2.5" /> Nouveau
            </button>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="p-4 space-y-2">
                {[...Array(3)].map((_, i) => <div key={i} className="h-16 bg-gray-100 rounded-lg animate-pulse" />)}
              </div>
            ) : tickets.length === 0 ? (
              <div className="py-12 px-4 text-center">
                <FaLifeRing className="w-8 h-8 text-gray-200 mx-auto mb-3" />
                <p className="text-sm text-gray-400">Aucun ticket</p>
                <p className="text-[0.6875rem] text-gray-300 mt-1">Créez-en un si vous avez une question</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-50">
                {tickets.map(t => {
                  const isSelected = selectedId === t.id;
                  const hasUnread = t.unread_by_user;
                  return (
                    <button
                      key={t.id}
                      onClick={() => { setSelectedId(t.id); setShowNewForm(false); }}
                      className={`w-full text-left px-4 py-3 transition-colors ${isSelected ? 'bg-gray-50' : 'hover:bg-gray-50'}`}
                      style={isSelected ? { borderLeft: `3px solid ${brand}`, paddingLeft: 13 } : {}}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5">
                            {hasUnread && <FaCircle className="w-2 h-2 flex-shrink-0" style={{ color: brand }} />}
                            <p className={`text-sm truncate ${hasUnread ? 'font-bold text-gray-900' : 'font-medium text-gray-700'}`}>
                              {t.subject}
                            </p>
                          </div>
                          {t.last_message && (
                            <p className="text-[0.6875rem] text-gray-400 mt-0.5 truncate">
                              {t.last_sender === 'admin' ? 'Fideliz: ' : 'Vous: '}{t.last_message}
                            </p>
                          )}
                        </div>
                        <div className="flex-shrink-0 text-right">
                          <p className="text-[0.625rem] text-gray-400">{formatDate(t.last_message_at || t.created_at)}</p>
                          {t.status === 'closed' && (
                            <span className="inline-flex items-center gap-0.5 text-[0.5625rem] text-green-600 mt-0.5">
                              <FaCheckCircle className="w-2 h-2" /> Fermé
                            </span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* ═══ RIGHT: Thread or new form ═══ */}
        <div className="flex-1 flex flex-col bg-gray-50 min-w-0">

          {showNewForm ? (
            <>
              <div className="px-6 py-3 border-b border-gray-200 bg-white flex items-center justify-between">
                <h3 className="text-sm font-bold text-gray-900">Nouveau ticket de support</h3>
                <button onClick={() => setShowNewForm(false)} className="p-1.5 hover:bg-gray-100 rounded-lg">
                  <FaTimes className="w-3.5 h-3.5 text-gray-400" />
                </button>
              </div>
              <div className="flex-1 overflow-y-auto p-6">
                <div className="max-w-2xl mx-auto bg-white rounded-2xl border border-gray-200 p-6 space-y-4">
                  <div>
                    <p className="text-[0.6875rem] font-semibold text-gray-500 mb-1.5">Sujet</p>
                    <input
                      value={newSubject}
                      onChange={e => setNewSubject(e.target.value)}
                      placeholder="Ex: Problème avec mon abonnement"
                      className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
                    />
                  </div>
                  <div>
                    <p className="text-[0.6875rem] font-semibold text-gray-500 mb-1.5">Votre message</p>
                    <textarea
                      value={newBody}
                      onChange={e => setNewBody(e.target.value)}
                      placeholder="Décrivez votre demande en détail..."
                      rows={8}
                      className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-gray-300"
                    />
                  </div>
                  <button
                    onClick={createTicket}
                    disabled={!newSubject.trim() || !newBody.trim() || creating}
                    className="w-full py-3 text-white text-sm font-semibold rounded-xl disabled:opacity-40 hover:opacity-90"
                    style={{ backgroundColor: brand }}
                  >
                    {creating ? 'Envoi...' : 'Envoyer la demande'}
                  </button>
                  <p className="text-[0.6875rem] text-gray-400 text-center">
                    L'équipe Fideliz vous répondra par email et directement dans ce ticket.
                  </p>
                </div>
              </div>
            </>
          ) : selectedId && thread ? (
            <>
              {/* Thread header */}
              <div className="px-6 py-3 border-b border-gray-200 bg-white flex items-center gap-3">
                <button onClick={() => setSelectedId(null)} className="lg:hidden p-1.5 hover:bg-gray-100 rounded-lg">
                  <FaArrowLeft className="w-3.5 h-3.5 text-gray-500" />
                </button>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-bold text-gray-900 truncate">{thread.ticket.subject}</h3>
                  <p className="text-[0.6875rem] text-gray-400 flex items-center gap-1.5 mt-0.5">
                    <FaClock className="w-2.5 h-2.5" />
                    Créé le {new Date(thread.ticket.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                    {thread.ticket.status === 'closed' && <span className="ml-2 text-green-600 font-medium">• Ticket fermé</span>}
                  </p>
                </div>
                {thread.ticket.status !== 'closed' && (
                  <button
                    onClick={closeTicket}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-[0.6875rem] font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:text-red-600 transition-colors"
                  >
                    <FaCheckCircle className="w-2.5 h-2.5" /> Clore le ticket
                  </button>
                )}
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-6">
                {threadLoading ? (
                  <p className="text-sm text-gray-300 text-center py-8">Chargement...</p>
                ) : (
                  <div className="max-w-3xl mx-auto space-y-4">
                    {thread.messages.map(m => {
                      const isPro = m.sender_type === 'pro';
                      return (
                        <div key={m.id} className={`flex ${isPro ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[75%] ${isPro ? 'items-end' : 'items-start'}`}>
                            <p className={`text-[0.625rem] font-semibold text-gray-400 uppercase mb-1 ${isPro ? 'text-right' : ''}`}>
                              {isPro ? 'Vous' : 'Équipe Fideliz'} · {formatMessageDate(m.created_at)}
                            </p>
                            <div
                              className={`px-4 py-3 rounded-2xl ${isPro ? 'text-white' : 'bg-white border border-gray-200 text-gray-800'}`}
                              style={isPro ? { backgroundColor: brand, borderBottomRightRadius: 6 } : { borderBottomLeftRadius: 6 }}
                            >
                              <p className="text-[0.8125rem] leading-relaxed whitespace-pre-wrap">{m.content}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={messagesEndRef} />
                  </div>
                )}
              </div>

              {/* Reply input */}
              {thread.ticket.status !== 'closed' && (
                <div className="px-6 py-4 border-t border-gray-200 bg-white">
                  <div className="max-w-3xl mx-auto flex items-end gap-3">
                    <textarea
                      value={newMessage}
                      onChange={e => setNewMessage(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                      placeholder="Tapez votre réponse..."
                      rows={2}
                      className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-gray-300"
                    />
                    <button
                      onClick={sendMessage}
                      disabled={!newMessage.trim() || sending}
                      className="flex-shrink-0 w-11 h-11 text-white rounded-xl hover:opacity-90 disabled:opacity-40 flex items-center justify-center"
                      style={{ backgroundColor: brand }}
                    >
                      <FaPaperPlane className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="w-16 h-16 rounded-2xl mx-auto mb-4 flex items-center justify-center" style={{ backgroundColor: brand + '10' }}>
                  <FaLifeRing className="w-7 h-7" style={{ color: brand }} />
                </div>
                <p className="text-sm font-semibold text-gray-900 mb-1">Support Fideliz</p>
                <p className="text-[0.75rem] text-gray-400 mb-5">Sélectionnez un ticket ou créez-en un nouveau</p>
                <button
                  onClick={() => { setShowNewForm(true); setSelectedId(null); }}
                  className="inline-flex items-center gap-2 px-5 py-2.5 text-white text-sm font-semibold rounded-xl hover:opacity-90"
                  style={{ backgroundColor: brand }}
                >
                  <FaPlus className="w-3 h-3" /> Nouveau ticket
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </ProLayout>
  );
};

export default SupportPage;
